import {MechanicValidator} from './MechanicValidator'
import {ActionStatus, PieceTypes} from '@colonist/game-base-types'
import {ArrayUtils} from '@colonist/utils'
import {ActionState, MechanicRoadPlayerState, MechanicRoadState, NestedReadonly, PlayerColor, TileEdgeType} from '@colonist/game-states'
import {getCardsForPiece} from '@colonist/game-card-types'
import {PlayerValidator} from '../core/PlayerValidator'
import {MapValidator} from '../core/map/MapValidator'
import {CurrentStateValidator} from '../core/CurrentStateValidator'

export class MechanicValidatorRoad extends MechanicValidator {
    private readonly currentStateValidator: CurrentStateValidator
    private readonly mechanicRoadState: NestedReadonly<MechanicRoadState>

    constructor(playerValidators: PlayerValidator[], mapValidator: MapValidator, currentStateValidator: CurrentStateValidator, mechanicRoadState: NestedReadonly<MechanicRoadState>) {
        super(playerValidators, mapValidator)

        this.currentStateValidator = currentStateValidator
        this.mechanicRoadState = mechanicRoadState
    }

    getRoadPlayerState(player: PlayerColor): NestedReadonly<MechanicRoadPlayerState> {
        if(player == PlayerColor.None) {
            return {
                bankRoadAmount: 0,
            }
        }
        const mechanicRoadPlayerState = this.mechanicRoadState[player]
        if(mechanicRoadPlayerState === undefined) throw new Error(`No road player state for player ${player}`)

        return mechanicRoadPlayerState
    }

    hasResourcesAndPiecesForRoad(player: PlayerColor, useResources: boolean): ActionStatus {
        const roadPlayerState = this.getRoadPlayerState(player)
        if(roadPlayerState.bankRoadAmount <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughRoadPieces'}}
        if(!useResources) return {canTakeAction: true}

        const resourcesForRoad = getCardsForPiece(PieceTypes.Road)
        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noPlayer'}}

        const hasResourcesForRoad = playerController.hasResourceCardsInHand(resourcesForRoad)
        if(!hasResourcesForRoad) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerPlaceRoad(player: PlayerColor, useResources: boolean): ActionStatus {
        const isSomePlayerDiscarding = this.currentStateValidator.isSomePlayerInActionState(ActionState.SelectCardsToDiscard)
        if(isSomePlayerDiscarding) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.playersDiscarding'}}

        const where = this.whereCanPlayerPlaceRoads(player)
        const doesPlayerHaveResourcesAndPiecesForRoad = this.hasResourcesAndPiecesForRoad(player, useResources)
        doesPlayerHaveResourcesAndPiecesForRoad.validHoverLocations = where
        if(!doesPlayerHaveResourcesAndPiecesForRoad.canTakeAction) return doesPlayerHaveResourcesAndPiecesForRoad

        if(where.length <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noWhereToPlaceRoad'}, validHoverLocations: where}

        return {canTakeAction: true, validHoverLocations: where}
    }

    whereCanPlayerPlaceRoads(player: PlayerColor): number[] {
        const placesToBuildRoadArray: number[] = []
        for(const corner of this.mapValidator.tileState.tileCorners) {
            if(corner.owner != player) continue
            if(corner.buildingType == undefined) continue
            if(this.mapValidator.isCornerKnightAndIsKnightNotTouchingEdgeType(player, corner, TileEdgeType.Road)) continue

            for(const edge of corner.hexCorner.touchingEdges()) {
                const tileEdge = this.mapValidator.tileState.getTileEdge(edge)
                if(tileEdge == undefined) continue
                if(!this.mapValidator.tileState.isEdgeTouchingLand(tileEdge)) continue
                if(tileEdge.type != undefined) continue

                const edgeIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge)
                if(edgeIndex == undefined) continue

                placesToBuildRoadArray.push(edgeIndex)
            }
        }

        for(const edge of this.mapValidator.tileState.tileEdges) {
            if(edge.owner != player) continue
            if(edge.type != TileEdgeType.Road) continue

            for(const endPoint of edge.hexEdge.endPoints()) {
                const tileCorner = this.mapValidator.tileState.getTileCorner(endPoint)
                if(tileCorner == undefined) continue
                if(tileCorner.owner != undefined) continue

                for(const touchingEdge of tileCorner.hexCorner.touchingEdges()) {
                    const tileEdge = this.mapValidator.tileState.getTileEdge(touchingEdge)
                    if(tileEdge == undefined) continue
                    if(!this.mapValidator.tileState.isEdgeTouchingLand(tileEdge)) continue
                    if(tileEdge.type != undefined) continue

                    const edgeIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge)
                    if(edgeIndex == undefined) continue

                    placesToBuildRoadArray.push(edgeIndex)
                }
            }
        }

        const uniqueRoadLocations = ArrayUtils.removeDuplicatesFromArray(placesToBuildRoadArray)
        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return uniqueRoadLocations

        const roadPlayerState = this.getRoadPlayerState(player)
        for(const restrictedPlacement of roadPlayerState.restrictedRoadPlacements ?? []) {
            ArrayUtils.removeElementFromArray(uniqueRoadLocations, restrictedPlacement)
        }

        return uniqueRoadLocations
    }

    whereCanPlayerPlaceBeginningRoads(player: PlayerColor): number[] {
        const placesToBuildRoadArray: number[] = []
        for(const corner of this.mapValidator.tileState.tileCorners) {
            if(corner.owner != player) continue
            if(corner.buildingType == undefined) continue
            if(!this.mapValidator.areAdjacentEdgesEmpty(corner)) continue

            for(const edge of corner.hexCorner.touchingEdges()) {
                const tileEdge = this.mapValidator.tileState.getTileEdge(edge)
                if(tileEdge == undefined) continue
                if(!this.mapValidator.tileState.isEdgeTouchingLand(tileEdge)) continue

                const tileIndex = this.mapValidator.tileState.getTileEdgeIndex(tileEdge.hexEdge)
                if(tileIndex == undefined) continue

                placesToBuildRoadArray.push(tileIndex)
            }
        }
        return placesToBuildRoadArray
    }
}
